// src/oura/components/newscan/QuestionRow.tsx
//
// One of the 50 on the Review step, about 44px: a status dot where the
// group's dot sits (rose: must fix, amber: to decide, with the word for a
// screen reader), the number, and the wording with each word it was built
// from underlined (dotted violet; solid blue for a word staff typed), each
// with its plain-words label on hover. After the wording: "Yours" on a
// reworded or swapped question ("Try another" taken) and the language on a
// Malay or Chinese one. On the right:
// the Search Console count when there is one (none means unknown, never 0).
// A locked question shows a small grey lock; locking and unlocking happen in
// the editor, so 50 rows do not carry 50 identical buttons.
//
// The wording is the button that opens the editor in place. On a read-only
// draft (approved or started) it is plain text.
//
// Brand rows (v4.5) add the market chip before the wording ("US", "Any") when
// the brand sells to more than 1 market and, after it, an amber mark only when
// no Google search is behind the question (a focusable button with a tooltip,
// tap to show on a phone). The search a row came from shows in its editor.

import { LockIcon, LockOpenIcon, MoreHorizontalIcon, PencilIcon, SearchIcon, SearchXIcon, ShuffleIcon, TagIcon } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@component/dropdown-menu";
import { useScanFlow } from "@oura/newscan/ScanFlowContext";
import type { ScanDraftQuestion, ScanDraftWord } from "@service/aeo/scanDraftTypes";
import { Tooltip, TooltipContent, TooltipRoot, TooltipTrigger } from "@component/tooltip";
import { cn } from "@oura/lib/cn";
import { Dot, FOCUS_RING, INSET_HAIRLINE, LEAD_W, Tag, TYPE } from "@oura/ui";
import { DECISION, fullCount, shortCount } from "@oura/newscan/copy";
import type { MarketName } from "./brandReview";
import { QuestionEditor } from "./QuestionEditor";
import { SlotText } from "./SlotText";

const COPY = {
    yours: "Yours",
    srNumber: (n: number) => `Question ${n}: `,
    language: { ms: "Malay", zh: "Chinese" } as Record<string, string>,
    locked: "Locked",
    lockedTip: "Locked. Word changes and redrafts keep this wording.",
    searches: (query: string, n: number) => `“${query}”: ${fullCount(n)} searches a year`,
    searchesShort: (n: number) => `${fullCount(n)} searches a year`,
    untraced: "No Google search behind this",
    edited: "Edited",
    editedV: (v: number) => `Edited · V${v}`,
    reasking: "Re-asking…",
    reaskingTip: "The wording was corrected on the scan. Its answers come back in a few minutes.",
    menu: (n: number) => `More for question ${n}`,
    edit: "Edit wording",
    tryAnother: "Try another wording",
    noOther: "No other wording fits",
    lock: "Keep this wording",
    lockHint: "Redrafts and word changes leave it alone",
    unlock: "Unlock",
    words: "Change a word",
} as const;

/**
 * A brand question with no Google search behind it: a focusable amber mark with
 * a tooltip. A traced row shows no mark (the health line says how many are
 * traced); its search shows in the editor's footnote when the row opens.
 */
function TraceMark({ q }: { q: ScanDraftQuestion }) {
    if (q.trace !== null) return null;
    return (
        <Tooltip content={COPY.untraced} asChild delayDuration={200}>
            <button
                type="button"
                aria-label={COPY.untraced}
                className={cn(
                    // 16px mark, 44px target drawn round it.
                    "relative mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded after:absolute after:-inset-3.5 after:content-['']",
                    FOCUS_RING,
                )}
            >
                <SearchXIcon aria-hidden size={12} className="text-amber-600" />
            </button>
        </Tooltip>
    );
}

/** A run of the wording: plain text, or the value of one of its words. */
interface Segment {
    text: string;
    variable: string | null;
}

/** A letter, digit or underscore in any script: what a word boundary is measured against. */
const isWordChar = (c: string | undefined): boolean => Boolean(c && /[\p{L}\p{N}_]/u.test(c));

/** Split the wording where the values of its words sit, longest value first, without overlaps. */
function segmentsOf(q: ScanDraftQuestion, words: Record<string, ScanDraftWord>): Segment[] {
    const text = q.text;
    const lower = text.toLowerCase();
    const taken: Array<{ start: number; end: number; variable: string }> = [];
    const values = Object.entries(q.fill_values)
        .filter(([v, value]) => value && value.trim().length >= 2 && (words[v] || q.fill_vars.includes(v)))
        .sort((a, b) => b[1].length - a[1].length);
    values.forEach(([variable, value]) => {
        const needle = value.toLowerCase();
        let from = 0;
        while (from <= lower.length) {
            const at = lower.indexOf(needle, from);
            if (at < 0) break;
            const end = at + needle.length;
            // Whole words only: "US" is never found inside "business" (Jack, #4285).
            const startsClean = !isWordChar(needle[0]) || !isWordChar(lower[at - 1]);
            const endsClean = !isWordChar(needle[needle.length - 1]) || !isWordChar(lower[end]);
            if (!startsClean || !endsClean) {
                from = at + 1;
                continue;
            }
            if (!taken.some((t) => at < t.end && end > t.start)) taken.push({ start: at, end, variable });
            from = end;
        }
    });
    taken.sort((a, b) => a.start - b.start);
    const out: Segment[] = [];
    let i = 0;
    taken.forEach((t) => {
        if (t.start > i) out.push({ text: text.slice(i, t.start), variable: null });
        out.push({ text: text.slice(t.start, t.end), variable: t.variable });
        i = t.end;
    });
    if (i < text.length) out.push({ text: text.slice(i), variable: null });
    return out;
}

/** A hover label. The trigger is plain text (inside the wording's button), so it adds no tab stop. */
function Tip({ content, children }: { content: ReactNode; children: ReactNode }) {
    return (
        <TooltipRoot delayDuration={200}>
            <TooltipTrigger asChild>{children}</TooltipTrigger>
            <TooltipContent side="top" className="max-w-[min(18rem,calc(100vw-2rem))] text-left">
                {content}
            </TooltipContent>
        </TooltipRoot>
    );
}

/** How a finished scan's question was corrected, for an edit of that scan (staff only). */
export interface QuestionEdit {
    version: number | null;
    /** One line per correction, newest first, for the tag's hover. */
    history: string;
    reasking: boolean;
}

export interface QuestionRowProps {
    question: ScanDraftQuestion;
    words: Record<string, ScanDraftWord>;
    /** An open decision on this question. */
    severity: "blocker" | "decide" | null;
    open: boolean;
    onToggle(): void;
    /** The editor closed; `saved` when a write landed. */
    onClose(saved: boolean): void;
    onOpenWord(variable: string): void;
    /** An approved or started draft: the wording is text, not a button. */
    readOnly?: boolean;
    /** Brand rows: the market the question is asked for, shown as a chip before the wording. */
    market?: MarketName | null;
    /** An edit of a finished scan: that question's correction there, or one running now. */
    edit?: QuestionEdit | null;
}

export function QuestionRow({
    question: q,
    words,
    severity,
    open,
    onToggle,
    onClose,
    onOpenWord,
    readOnly = false,
    market = null,
    edit = null,
}: QuestionRowProps) {
    const editorId = `q-edit-${q.n}`;
    const { ctl } = useScanFlow();
    // The menu's "Try another wording" opens the editor with the alternatives loading.
    const [autoTry, setAutoTry] = useState(false);
    useEffect(() => {
        if (!open) setAutoTry(false);
    }, [open]);
    const vars = Array.from(new Set(q.reworded ? [] : q.fill_vars)).filter((v) => words[v]);
    const canTry = !q.locked && !q.reworded && q.alt_count > 0;
    const segments = q.reworded ? [{ text: q.text, variable: null }] : segmentsOf(q, words);
    const hit = q.search;
    const top = hit?.queries[0] ?? null;
    const lang = COPY.language[q.language];
    const title = (
        <>
            <span className="sr-only">{COPY.srNumber(q.n)}</span>
            {severity && <span className="sr-only">{DECISION.severity[severity]}: </span>}
            {market && (
                <span className="mr-1.5 inline-flex align-middle">
                    <Tag tone="gray" className="tabular-nums">
                        <span aria-hidden>{market.short}</span>
                        <span className="sr-only">{market.full}, </span>
                    </Tag>
                </span>
            )}
            <span className="break-words">
                {segments.map((s, i) => {
                    if (!s.variable)
                        return (
                            <span key={i}>
                                <SlotText text={s.text} words={words} />
                            </span>
                        );
                    const word = words[s.variable];
                    const staff = word?.origin === "staff";
                    return (
                        <Tip key={i} content={word?.label ?? s.variable}>
                            <span
                                className={cn(
                                    "underline decoration-2 underline-offset-[3px]",
                                    staff ? "decoration-blue-500 decoration-solid" : "decoration-violet-300 decoration-dotted",
                                )}
                            >
                                {s.text}
                            </span>
                        </Tip>
                    );
                })}
            </span>
            {edit && (
                <span className="ml-1.5 inline-flex align-middle">
                    {edit.reasking ? (
                        <Tip content={COPY.reaskingTip}>
                            <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200">{COPY.reasking}</span>
                        </Tip>
                    ) : (
                        <Tip content={edit.history}>
                            <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
                                {edit.version ? COPY.editedV(edit.version) : COPY.edited}
                            </span>
                        </Tip>
                    )}
                </span>
            )}
            {(q.reworded || q.swapped || lang) && (
                <span className="ml-1.5 inline-flex items-center gap-1.5 align-middle">
                    {(q.reworded || q.swapped) && !edit && <Tag tone="blue">{COPY.yours}</Tag>}
                    {lang && <Tag tone="gray">{lang}</Tag>}
                </span>
            )}
            {q.locked && (
                <Tip content={COPY.lockedTip}>
                    <span className="ml-1.5 inline-flex align-middle text-gray-400">
                        <LockIcon aria-hidden size={12} />
                        <span className="sr-only">, {COPY.locked}</span>
                    </span>
                </Tip>
            )}
        </>
    );

    return (
        <div data-q-row={q.n} className={cn(INSET_HAIRLINE("dot"), "group")}>
            <div className="flex min-h-11 items-start gap-3 py-2.5">
                <span className={cn("flex h-5 shrink-0 items-center justify-center", LEAD_W.dot)}>
                    {severity && <Dot tone={severity === "blocker" ? "rose" : "amber"} />}
                </span>
                {/* The number opens the editor too (Angel, 15 Oct). Not a second tab stop: the wording's button says the number. */}
                <span
                    aria-hidden
                    onClick={readOnly ? undefined : onToggle}
                    className={cn(TYPE.tc, "w-6 shrink-0 pt-px text-right tabular-nums text-gray-400", !readOnly && "cursor-pointer hover:text-blue-700")}
                >
                    {q.n}
                </span>
                <div className="flex min-w-0 flex-1 items-start gap-2">
                    {readOnly ? (
                        <div className={cn(TYPE.tb, "min-w-0 flex-1 text-gray-900")}>{title}</div>
                    ) : (
                        <button
                            type="button"
                            data-q-title={q.n}
                            aria-expanded={open}
                            aria-controls={open ? editorId : undefined}
                            onClick={onToggle}
                            className={cn(
                                TYPE.tb,
                                "relative block min-w-0 flex-1 rounded text-left text-gray-900 motion-safe:transition-colors hover:text-blue-700",
                                "after:absolute after:-inset-y-2.5 after:inset-x-0 after:content-['']",
                                FOCUS_RING,
                            )}
                        >
                            {title}
                        </button>
                    )}
                    <TraceMark q={q} />
                </div>
                {!readOnly && (
                    // Every action on a question in one place (Jovi, 20 Oct): before, Try another and
                    // Lock sat inside the editor and a word could only be reached from there.
                    <DropdownMenu modal={false}>
                        <DropdownMenuTrigger
                            aria-label={COPY.menu(q.n)}
                            className={cn(
                                "-my-1 grid h-7 w-7 shrink-0 place-items-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 data-[state=open]:bg-gray-100 data-[state=open]:text-gray-700",
                                "opacity-0 focus-visible:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100 [@media(hover:none)]:opacity-100 motion-safe:transition-opacity",
                                open && "opacity-100",
                                FOCUS_RING,
                            )}
                        >
                            <MoreHorizontalIcon aria-hidden size={16} />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-72">
                            <DropdownMenuItem onSelect={() => !open && onToggle()}>
                                <PencilIcon aria-hidden />
                                {COPY.edit}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                disabled={!canTry}
                                onSelect={() => {
                                    setAutoTry(true);
                                    if (!open) onToggle();
                                }}
                            >
                                <ShuffleIcon aria-hidden />
                                {canTry ? COPY.tryAnother : COPY.noOther}
                            </DropdownMenuItem>
                            <DropdownMenuItem disabled={ctl.busy !== null} onSelect={() => void ctl.patchQuestion(q.n, { locked: !q.locked })}>
                                {q.locked ? <LockOpenIcon aria-hidden /> : <LockIcon aria-hidden />}
                                <span className="min-w-0">
                                    {q.locked ? COPY.unlock : COPY.lock}
                                    {!q.locked && <span className="block text-xs text-gray-500">{COPY.lockHint}</span>}
                                </span>
                            </DropdownMenuItem>
                            {vars.length > 0 && (
                                <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuLabel className="text-xs font-medium text-gray-500">{COPY.words}</DropdownMenuLabel>
                                    {vars.map((v) => (
                                        <DropdownMenuItem key={v} onSelect={() => onOpenWord(v)} className="items-start">
                                            <TagIcon aria-hidden className="mt-0.5" />
                                            {/* In full, on two lines: cut at "…" the word itself was the part hidden (Jovi, 20 Oct). */}
                                            <span className="min-w-0 break-words">
                                                <span className="block text-xs text-gray-500">{words[v].label}</span>
                                                <span className="block font-medium text-gray-900">{q.fill_values[v] ?? words[v].value}</span>
                                            </span>
                                        </DropdownMenuItem>
                                    ))}
                                </>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
                {hit && (
                    <Tip content={top ? COPY.searches(top.query, top.searches) : COPY.searchesShort(hit.searches)}>
                        <span className="mt-px inline-flex shrink-0 cursor-default">
                            <Tag tone="blue" className="tabular-nums">
                                <SearchIcon aria-hidden size={12} />
                                <span aria-hidden>{shortCount(hit.searches)}</span>
                                <span className="sr-only">
                                    {top ? COPY.searches(top.query, top.searches) : COPY.searchesShort(hit.searches)}
                                </span>
                            </Tag>
                        </span>
                    </Tip>
                )}
            </div>
            {open && (
                <div className="mb-3 ml-5">
                    <QuestionEditor
                        question={q}
                        words={words}
                        id={editorId}
                        autoTry={autoTry}
                        onClose={(saved) => {
                            setAutoTry(false);
                            onClose(saved);
                        }}
                        onOpenWord={onOpenWord}
                    />
                </div>
            )}
        </div>
    );
}
